/**
 * /pages/wishlist (sections/pag-wishlist-grid.liquid).
 *
 * Two sources for the same render path: the shopper's own device list
 * (pag-personal-store's `saved`), or — with `?ids=1,2,3` in the URL, as
 * built by the Share button below — a read-only view of someone else's
 * list. Neither needs a backend: a shared list is just ids read back out
 * of a URL, resolved through the same Section Rendering API hydrate as
 * every other PUE surface.
 */

import { saved, CHANGE_EVENT } from '@theme/pag-personal-store';
import { fetchProductCards, announceHydrated } from '@theme/pag-product-hydrate';

/** @returns {string[] | null} ids from ?ids=1,2,3, or null if the param is absent/empty */
function sharedIdsFromUrl() {
  const raw = new URLSearchParams(window.location.search).get('ids');
  if (!raw) return null;
  const ids = raw
    .split(',')
    .map((id) => id.trim())
    .filter((id) => /^\d+$/.test(id));
  return ids.length > 0 ? ids : null;
}

function brandOf(card) {
  return card.querySelector('.pag-mini-card__brand')?.textContent?.trim().toLocaleLowerCase() ?? '';
}

class WishlistPage {
  constructor(root) {
    this.root = root;
    this.list = root.querySelector('[data-pag-wishlist-page-list]');
    this.empty = root.querySelector('[data-pag-wishlist-page-empty]');
    this.controls = root.querySelector('[data-pag-wishlist-page-controls]');
    this.count = root.querySelector('[data-pag-wishlist-page-count]');
    this.notice = root.querySelector('[data-pag-wishlist-page-notice]');
    this.sortSelect = root.querySelector('[data-pag-wishlist-page-sort]');
    this.shareButton = root.querySelector('[data-pag-wishlist-page-share]');
    this.clearButton = root.querySelector('[data-pag-wishlist-page-clear]');
    if (!this.list) return;

    this.sharedIds = sharedIdsFromUrl();
    this.cards = [];

    this.sortSelect?.addEventListener('change', () => this.render());
    this.shareButton?.addEventListener('click', () => this.share());
    this.clearButton?.addEventListener('click', () => this.clearAll());

    if (!this.sharedIds) {
      document.addEventListener(CHANGE_EVENT, (event) => {
        if (event.detail?.list === 'saved') this.load();
      });
    }

    this.load();
  }

  get ownIds() {
    return saved.all().map((entry) => entry.id);
  }

  async load() {
    const ids = this.sharedIds ?? this.ownIds;

    if (this.notice) {
      if (this.sharedIds) {
        this.notice.hidden = false;
        this.notice.textContent = "You're viewing a wishlist someone shared with you. Heart an item to save it to your own.";
      } else {
        this.notice.hidden = true;
      }
    }
    if (this.clearButton) this.clearButton.hidden = Boolean(this.sharedIds);

    if (ids.length === 0) {
      this.showEmpty();
      return;
    }

    let cards;
    try {
      cards = await fetchProductCards(ids);
    } catch {
      return;
    }

    if (cards.length === 0) {
      this.showEmpty();
      return;
    }

    this.cards = cards;
    this.render();
  }

  showEmpty() {
    this.cards = [];
    if (this.list) {
      this.list.hidden = true;
      this.list.replaceChildren();
    }
    if (this.empty) this.empty.hidden = false;
    if (this.controls) this.controls.hidden = true;
    if (this.count) this.count.hidden = true;
  }

  render() {
    if (!this.list || this.cards.length === 0) return;

    const sortBy = this.sortSelect?.value ?? 'recent';
    const ordered =
      sortBy === 'brand' ? [...this.cards].sort((a, b) => brandOf(a).localeCompare(brandOf(b))) : this.cards;

    this.list.replaceChildren(
      ...ordered.map((card) => {
        const item = document.createElement('li');
        item.className = 'pag-wishlist-grid__item';
        item.appendChild(card.cloneNode(true));
        return item;
      })
    );
    this.list.hidden = false;
    if (this.empty) this.empty.hidden = true;
    if (this.controls) this.controls.hidden = false;
    if (this.count) {
      this.count.hidden = false;
      this.count.textContent = `${this.cards.length} ${this.cards.length === 1 ? 'item' : 'items'} — saved on this device`;
    }
    announceHydrated(this.list);
  }

  async share() {
    const ids = this.sharedIds ?? this.ownIds;
    if (ids.length === 0) return;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('ids', ids.join(','));

    try {
      await navigator.clipboard.writeText(url.toString());
      this.flashShare('Link copied');
    } catch {
      window.prompt('Copy this link:', url.toString());
    }
  }

  flashShare(message) {
    if (!this.shareButton) return;
    const original = this.shareButton.textContent;
    this.shareButton.textContent = message;
    window.setTimeout(() => {
      if (this.shareButton) this.shareButton.textContent = original;
    }, 1600);
  }

  clearAll() {
    saved.clear();
  }
}

document.querySelectorAll('.pag-wishlist-grid').forEach((root) => new WishlistPage(root));
